import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { isAppPromoExcludedPath } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/appPromoUtils'
import { IDENTITY_PROVIDERS } from '@lyrahealth-inc/shared-app-logic/src/features/registration/constants'
import { useGetIsMobileWebBrowser } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useGetIsMobileWebBrowser'

import { getLwMobileEnabled } from '../../data/customer/customerSelectors'
import { getSsoIdentityProvider } from '../../data/register/registerSelectors'
import { MOBILE_PROMO_DISMISSED } from '../constants/appConstants'

export interface useShowAppPromoArgs {
  promoBottomSheetEnabled?: boolean
}

export const useShowAppPromo = (
  { promoBottomSheetEnabled }: useShowAppPromoArgs = { promoBottomSheetEnabled: false },
) => {
  const isMobileBrowser = useGetIsMobileWebBrowser()
  const isLwMobileAppEnabled = useSelector(getLwMobileEnabled)
  const ssoIdentityProvider = useSelector(getSsoIdentityProvider)
  const [showAppPromoBanner, setShowAppPromoBanner] = useState(false)
  const [showAppPromoBottomSheet, setShowAppPromoBottomSheet] = useState(false)
  const { pathname } = useLocation()

  const isInExcludedPath = isAppPromoExcludedPath(pathname)

  const PARTNER_SSO_EXCLUDED_PATHS = [PAGE_ROUTES.REGISTER_ACCOUNT_SETUP]

  // If the user is coming from a partner SSO, we don't want to show the app promo banner on the registration page.
  const isInPartnerSSOExcludedPath =
    ssoIdentityProvider === IDENTITY_PROVIDERS.PARTNER &&
    PARTNER_SSO_EXCLUDED_PATHS.some((path) => pathname.includes(path))

  useEffect(() => {
    if (!isInExcludedPath && isMobileBrowser && isLwMobileAppEnabled && !isInPartnerSSOExcludedPath) {
      const promoBottomSheetDismissed = window.localStorage.getItem(MOBILE_PROMO_DISMISSED) === 'true'
      setShowAppPromoBanner(!promoBottomSheetEnabled || (promoBottomSheetEnabled && promoBottomSheetDismissed))
      if (promoBottomSheetEnabled && !promoBottomSheetDismissed) {
        const timeout = setTimeout(() => {
          setShowAppPromoBottomSheet(true)
        }, 2000)
        return () => clearTimeout(timeout)
      }
    } else {
      setShowAppPromoBottomSheet(false)
      setShowAppPromoBanner(false)
    }
    return
  }, [
    isMobileBrowser,
    isLwMobileAppEnabled,
    promoBottomSheetEnabled,
    setShowAppPromoBanner,
    setShowAppPromoBottomSheet,
    isInExcludedPath,
    isInPartnerSSOExcludedPath,
    ssoIdentityProvider,
  ])

  return {
    showAppPromoBanner,
    setShowAppPromoBanner,
    showAppPromoBottomSheet,
    setShowAppPromoBottomSheet,
  }
}
